import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import AddHomeWorkIcon from '@mui/icons-material/AddHomeWork'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import CropFreeIcon from '@mui/icons-material/CropFree'
import EditIcon from '@mui/icons-material/Edit'
import TuneIcon from '@mui/icons-material/Tune'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import Router from 'next/router'
import { parseCookies, setCookie } from 'nookies'
import { useSnackbar } from 'notistack'
import WhatsappIcon from 'public/svgs/whatsapp-icon.svg'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { UnavailableReason } from 'src/components/Details/UnavailableBanner'
import LeadForm from 'src/components/LeadForm'
import { buildLeadFormWaMessage } from 'src/components/LeadForm/buildLeadFormWaMessage'
import type { LeadFormValues } from 'src/components/LeadForm/useLeadFormState'
import OperatorPromoNote from 'src/components/OperatorPromo/OperatorPromoNote'
import PromoBadge, { promoBadgeContent } from 'src/components/Promo/PromoBadge'
import { useCurrencyAndLanguageProvider } from 'src/context/CurrencyAndLanguageProvider'
import useLogger from 'src/context/LoggerProvider/useLogger'
import useErrorSnackbar from 'src/hooks/useErrorSnackbar'
import { useGetPrice } from 'src/hooks/usePrice'
import useTrackEvent from 'src/hooks/useTrackEvent'
import { baseFleetVariant, useListingVariant } from 'src/lib/listingVariants'
import type {
  LeadFormChannel,
  TrustSignalKey
} from 'src/lib/listingVariants/types'
import { readReservationIntentForConfig } from 'src/lib/reservationIntent'
import {
  CustomerType,
  DealType,
  IConfiguration,
  ILocation,
  IMutation,
  IMutationCreateCheckoutArgs,
  IMutationSendCustomerInfoEmailArgs,
  Interest,
  LocationType,
  StorageTime
} from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { numberRounedUpWithoutDecimals } from 'src/utils'
import { runAfterNextPaint } from 'src/utils/afterNextPaint'
import { getDiscount } from 'src/utils/coverPhoto'
import { operatorPromoPill } from 'src/utils/operatorPromo'
import useAddToCart, { useGenerateLead } from 'src/utils/pixels/common'
import { firedOncePerSession } from 'src/utils/pixels/sessionDedup'
import { getEffectiveDealType, isMonthlyVariant } from 'src/utils/pricing'
import { getClickIdsFromCookies } from 'src/utils/utms'
import { claimWaSessionToken } from 'src/utils/waSessionToken'
import { buildWhatsappUrl } from 'src/utils/whatsapp'
import { useAuthProvider } from '../../context/AuthProvider'
import { useLoaderProvider } from '../../context/LoaderProvider'
import BaseButton from '../Buttons/BaseButton'
import ModalDrawer from '../ModalDrawer'
import { InputNumber } from './InputNumber'
import { getActiveScarcityCount } from './utils'

const MOBILE_STORAGE_CHIPS: { label: string; value: StorageTime }[] = [
  { label: '1-3m', value: StorageTime.FewMonths },
  { label: '3-5m', value: StorageTime.FewMonthsSix },
  { label: '6-12m', value: StorageTime.FewMonthsTwelve },
  { label: '1+ año', value: StorageTime.FewYears }
]

// Admin-only modal. Code-split so non-admin sessions don't pay the cost
// of pulling in EditConfig + the admin GraphQL surface.
const AdminEdit = dynamic(() => import('./AdminEdit'))

/**
 * Unit 15 — TrustSignalKey → { icon, label } mapping.
 *
 * Inline switch keeps every t() call on a literal locale key (typed t()
 * rejects dynamic strings). Adding a new TrustSignalKey requires both a
 * union entry in `src/lib/listingVariants/types.ts` and a branch here.
 */
function renderTrustSignal(
  signal: TrustSignalKey,
  t: ReturnType<typeof useTranslation<'details'>>['t']
): { icon: string; label: string } | null {
  switch (signal) {
    case 'now.no_charge':
      return { icon: 'si-circle-check', label: t('TRUST.NOW.NO_CHARGE') }
    case 'now.response_time':
      return { icon: 'si-hours', label: t('TRUST.NOW.RESPONSE_TIME') }
    case 'now.free_cancel':
      return { icon: 'si-house-shield', label: t('TRUST.NOW.FREE_CANCEL') }
    case 'broker.no_commitment':
      return {
        icon: 'si-circle-check',
        label: t('TRUST.BROKER.NO_COMMITMENT')
      }
    case 'broker.specialist':
      return { icon: 'si-user-headset', label: t('TRUST.BROKER.SPECIALIST') }
    case 'broker.custom_terms':
      return { icon: 'si-sliders', label: t('TRUST.BROKER.CUSTOM_TERMS') }
    case 'traditional.no_commitment':
      return {
        icon: 'si-circle-check',
        label: t('TRUST.TRADITIONAL.NO_COMMITMENT')
      }
    case 'traditional.free_advisor':
      return {
        icon: 'si-user-headset',
        label: t('TRUST.TRADITIONAL.FREE_ADVISOR')
      }
    case 'traditional.verified_spaces':
      return {
        icon: 'si-house-shield',
        label: t('TRUST.TRADITIONAL.VERIFIED_SPACES')
      }
    default:
      return null
  }
}

const NextButton = ({
  onClickBack,
  onClickNext
}: {
  onClickBack?: () => void
  onClickNext?: () => void
}) => {
  return (
    <div className="flex h-10 w-full overflow-hidden bg-blue-500">
      <button
        type="button"
        className="flex items-center justify-center border-r border-white-500/25 px-4 transition-colors hover:bg-blue-600"
        onClick={onClickBack}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} className="text-white-500" />
      </button>
      <button
        type="button"
        className="flex w-full items-center justify-center font-display text-[11px] tracking-[0.16em] text-white-500 uppercase transition-colors hover:bg-blue-600"
        onClick={onClickNext}
      >
        Siguiente
      </button>
    </div>
  )
}

const CREATE_CHECKOUT = gql`
  mutation CreateCheckout(
    $locationId: String!
    $configurationId: String!
    $saveCheckout: SaveCheckoutInput!
  ) {
    createCheckout(
      locationId: $locationId
      configurationId: $configurationId
      saveCheckout: $saveCheckout
    ) {
      checkout {
        id
      }
    }
  }
`

// Mutation for the unified LeadForm submission. Same backend handler as
// the legacy broker form.
const SEND_CUSTOMER_INFO_EMAIL = gql`
  mutation SendCustomerInfoEmail($customerInfo: CustomerInfoInput!) {
    sendCustomerInfoEmail(customerInfo: $customerInfo)
  }
`

interface PriceCardProps {
  location: ILocation
  adminPageDisplay?: boolean
  hideMobilePriceCard?: boolean
  hideDesktopPriceCard?: boolean
  activeConfig: IConfiguration
  quantity: number
  setQuantity: (q: number) => void
  storageTime: StorageTime | ''
  setStorageTime: (s: StorageTime | '') => void
  /**
   * When set, the normal CTAs (Reservar / LeadForm / WA pill) are replaced
   * by a slim unavailable card that points the renter at SimilarLocations.
   * See `UnavailableBanner` + root CLAUDE.md → "Unavailable listings".
   */
  unavailableReason?: UnavailableReason | null
  /**
   * DOM id of the SimilarLocations section, used by the unavailable card's
   * primary CTA to smooth-scroll the renter into the alternatives rail.
   */
  similarSectionId?: string
}

const PriceCard = ({
  location,
  adminPageDisplay,
  hideMobilePriceCard,
  hideDesktopPriceCard,
  activeConfig,
  quantity,
  setQuantity,
  storageTime,
  setStorageTime,
  unavailableReason,
  similarSectionId
}: PriceCardProps) => {
  const { submittedBy, id } = location
  const { size, availability } = activeConfig as IConfiguration

  const { t } = useTranslation('details')
  const { t: tCommon } = useTranslation('common')
  const { setRedirecting } = useLoaderProvider()
  const { enqueueSnackbar } = useSnackbar()
  const enqueueError = useErrorSnackbar()
  const { currentUser, toggleLoginModal, isAuthenticated, loadingCurrentUser } =
    useAuthProvider()
  const logger = useLogger()

  // Unified LeadForm state. Replaces the old `isBrokerModeModalOpen` —
  // the form now lives inline on desktop and in a bottom sheet on mobile.
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false)
  // True when the LeadForm drawer was opened by the desktop
  // "Solicitar por correo" CTA on a Broker/Traditional listing (the
  // email-secondary path on the WA-pill variants). Drives the
  // `primaryChannelOverride='email'` flip on <LeadForm> so the drawer
  // opens with the blue email pill on top and the WhatsApp link as the
  // secondary — matches the user's explicit channel pick. Resets to
  // false on close so the Fleet-pill flow that re-uses the same drawer
  // stays on its variant default (`primaryChannel: 'whatsapp'`).
  const [isLeadFormFromEmailCta, setIsLeadFormFromEmailCta] = useState(false)
  const [isFleetMode, setIsFleetMode] = useState(false)
  const [settingQuantity, setSettingQuantity] = useState(false)
  const [breakdownOpen, setBreakdownOpen] = useState(false)
  const [adminExpanded, setAdminExpanded] = useState(false)
  const configurationId = activeConfig?.id ?? ''

  // Re-entry continuity — when an unauthed visitor previously started a
  // /reservar for THIS Monthly listing (a reservationIntent snapshot
  // survives in localStorage), relabel the primary CTA "Continúa tu reserva"
  // instead of the cold "Reservar". SSR-safe: defaults false so the server
  // HTML and first client render both show "Reservar"; the effect flips it
  // post-hydration (no React 19 #418 risk). Catches returns that land on the
  // listing page (bookmark, share, chatbot fallback) rather than landing
  // directly on /reservar. `handleOnCheckout` already routes unauthed Monthly
  // visitors to /reservar, where the phone is now prefilled + the resume
  // banner shows — so the label change is the only thing needed here.
  const [resumeMonthly, setResumeMonthly] = useState(false)
  useEffect(() => {
    if (
      isAuthenticated ||
      !configurationId ||
      !isMonthlyVariant(activeConfig)
    ) {
      setResumeMonthly(false)
      return
    }
    setResumeMonthly(!!readReservationIntentForConfig(configurationId))
  }, [isAuthenticated, configurationId, activeConfig])

  const needsVerification =
    !currentUser?.isVerified && !currentUser?.requestedVerification

  const [createCheckout, { loading }] = useMutation<
    Pick<IMutation, 'createCheckout'>,
    IMutationCreateCheckoutArgs
  >(CREATE_CHECKOUT, {
    onCompleted: ({ createCheckout: { checkout } }) => {
      Router.push(`${RedirectUrls.Checkout}/${checkout.id}`)
    },
    onError: (error) => {
      setRedirecting(false)
      enqueueError(error, { logMessage: 'Error creating checkout.' })
    }
  })

  const [sendCustomerInfoEmail] = useMutation<
    Pick<IMutation, 'sendCustomerInfoEmail'>,
    IMutationSendCustomerInfoEmailArgs
  >(SEND_CUSTOMER_INFO_EMAIL)
  const { trackEvent, EventAction } = useTrackEvent()

  const notAvailable = (availability ?? 0) <= 0
  const userIsOwner = currentUser?.id === submittedBy

  const { currentCurrency } = useCurrencyAndLanguageProvider()
  const { getPrice } = useGetPrice()
  const fprice = getPrice(location, activeConfig)
  // Variant-driven mode flags. Replaces the previous inline
  // `getIsBrokerMode` / `dealType === Traditional` branches. Names preserved
  // (`isBrokerMode`, `isTraditionalMode`) so the 38+ downstream references
  // throughout this file keep working unchanged.
  const variant = useListingVariant()
  const isBrokerMode = variant.meta.variantKey === 'broker'
  const isTraditionalMode = variant.meta.variantKey === 'traditional'
  // Fleet pill verdict is resolver-owned since 2026-08-20 (Monthly OR
  // Traditional parking + availability over the fleet threshold — the
  // Entra onboarding added the first Traditional parking listings, and
  // fleet renters landing there deserve the flotillas lane too). Broker
  // stays excluded: its own form is already the B2B path. Gate source of
  // truth: resolveListingVariant.ts → priceCard.showFleetCta.
  const showFleetTrigger = variant.priceCard.showFleetCta
  const handleAddToCart = useAddToCart()
  // Fires GA4/Meta/Bing/LinkedIn/PostHog pixel AND writes LEAD_SUBMIT
  // (subType='broker'|'traditional_contact') to internal ES so the
  // marketing-report Excel attributes listing-side LeadForm submits to
  // the Pro / Traditional funnel tabs even when ADD_TO_CART doesn't fire
  // (desktop always-mounted LeadForm gap — see analytics-reference.md).
  const generateLead = useGenerateLead()
  // The listing's promo regardless of the chip picked, and the promo for the
  // chip actually picked. They differ when the renter chose a stay under 6
  // months: the card must then show the regular price plus a hint of which
  // stay unlocks it, never a −50% the checkout won't bill (2026-09-30 audit).
  const listingDiscount = getDiscount(activeConfig, isBrokerMode)
  const discount = storageTime
    ? getDiscount(activeConfig, isBrokerMode, { storageTime })
    : listingDiscount
  const discountNeedsLongerStay = Boolean(listingDiscount) && !discount
  const hasDiscount = !!discount
  const discountFraction = hasDiscount ? discount.percentage / 100 : 0

  // --- Lead form prefill from the persisted `lead` cookie ---------------
  // Same cookie shape used by the legacy broker form so returning
  // visitors don't re-type their name/phone/email between listings.
  // Also seeds `when` (storageTime) + `quantity` from PriceCard state so
  // the LeadForm Step 1 mount pixel fires with the full enrichment —
  // `duration_bucket` + `requested_months` ride on the canonical
  // add_to_cart even when the user submits the modal without touching a
  // step. Primary objective tier is long-stay (6+ month) deals.
  const leadFormPrefill = useMemo<Partial<LeadFormValues>>(() => {
    const base: Partial<LeadFormValues> = {
      when: storageTime || null,
      quantity
    }
    if (typeof window === 'undefined') return base
    const raw = parseCookies(null).lead
    if (!raw) return base
    try {
      const lead = JSON.parse(raw) as Record<string, unknown>
      return {
        ...base,
        name: typeof lead.name === 'string' ? lead.name : '',
        phone: typeof lead.phone === 'string' ? lead.phone : '',
        email: typeof lead.email === 'string' ? lead.email : '',
        additionalInfo:
          typeof lead.additionalInfo === 'string' ? lead.additionalInfo : ''
      }
    } catch {
      return base
    }
  }, [storageTime, quantity])

  const handleOnBrokerClick = () => {
    // `skipPixelFanout: true` — the LeadForm's Step 1 useEffect fires the
    // canonical `addToCart` pixel when the drawer mounts. Firing here too
    // would double GA4/Meta/UET counts. The internal ES `trackEvent` +
    // Clarity still fire (LeadForm doesn't dispatch those).
    handleAddToCart(
      isTraditionalMode ? 'traditional_contact' : 'broker_contact',
      location,
      activeConfig,
      quantity,
      storageTime,
      { skipPixelFanout: true }
    )
    setIsFleetMode(false)
    setIsLeadFormOpen(true)
  }

  const handleOnFleetClick = () => {
    // Same rationale as `handleOnBrokerClick` — LeadForm Step 1 owns the
    // pixel fanout; we only fire the ES event + Clarity here.
    handleAddToCart(
      'broker_contact',
      location,
      activeConfig,
      quantity,
      storageTime,
      { skipPixelFanout: true }
    )
    setIsFleetMode(true)
    setIsLeadFormOpen(true)
  }

  /**
   * Primary WA-direct path for Broker + Traditional variants.
   *
   * Replaces the 2026-05-11 overhaul's inline `<LeadForm>` mount on the
   * desktop right rail + sheet-on-tap on mobile. Production PostHog data
   * over the 2 weeks following that ship showed engagement→submit at
   * 0.6–0.8% (Broker) / 0.6–2.5% (Traditional) vs 75% click→qualified-
   * lead on the WA pill that the same overhaul also introduced. Plan:
   * docs/plans/2026-05-26-001-feat-listing-leadform-removal-plan.md.
   *
   * What fires on click (full pixel fanout — no `skipPixelFanout` flag
   * because the LeadForm engagement event no longer fires for these
   * variants; the click is the SOLE emitter):
   *   - `handleAddToCart` → ES `ADD_TO_CART` + PostHog `add_to_cart_intent`
   *     + GA4/Meta/UET/LinkedIn pixel fanout (`contact_wa` umbrella +
   *     segmented `contact_wa_storage_broker` / `_traditional` etc.)
   *   - `trackEvent({ action: BrokerSignup, subType })` — internal ES event
   *     for the marketing-report Excel funnel attribution
   *   - `generateLead({ method, channel: 'whatsapp' })` — GA4
   *     `generate_lead` + Meta `Lead` + Bing UET + LinkedIn (segmented per
   *     variant via method)
   *
   * Routing: corporate WA via `buildWhatsappUrl(msg)` with NO recipient
   * arg. Preserves the trust-face/destination split rule (see
   * spotme-client/CLAUDE.md → "WhatsApp routing — trust face vs
   * destination number"). The chatbot triages, LangGraph `complete` node
   * fires `qualified_whatsapp_lead` + Google Ads / Meta CAPI / Microsoft
   * Ads enhanced-conversion uploads server-side if the conversation
   * qualifies.
   *
   * The WA prefill (`buildLeadFormWaMessage`) seeds:
   *   - listing URL with `?sid={hashSessionToken}` (analytics join key)
   *   - listing URL preserves `gclid` / `fbclid` / `msclkid` query params
   *     after the 2026-05-26 client change so the backend
   *     `extractAttributionFromMessages` can close the Google Ads /
   *     Meta / Microsoft conversion loop for paid-traffic users
   *   - `Ref: {nanoId}` line
   *   - sqm seeded server-side from `configuration.size` (no need to
   *     ask the renter to re-type a value the listing already knows)
   */
  const handleOnWhatsappCta = () => {
    const subEvent: 'broker_contact' | 'traditional_contact' = isTraditionalMode
      ? 'traditional_contact'
      : 'broker_contact'
    const method = isTraditionalMode ? 'traditional_contact' : 'broker'

    // Fire the analytics stack ONCE per (session × listing × subEvent). The WA
    // pill can be re-triggered many times in a single session — a stuck in-app
    // WebView tab fired this 86× in 25 min on 2026-07-09, each fire counting as
    // a storage conversion and poisoning Google Ads Smart Bidding. `window.open`
    // below is intentionally OUTSIDE the guard: every click must still open
    // WhatsApp. Mirrors the LeadForm engagement dedup (atc_fired / onFocusCapture).
    // The fanout runs after the tap's paint (Jules, 2026-10-28); the
    // `window.open` below stays synchronous (popup blockers) and hiding the
    // page flushes the deferred fanout — see runAfterNextPaint.
    const waDedupKey = `wa_cta:${activeConfig?.id ?? 'none'}:${subEvent}`
    runAfterNextPaint(() => {
      if (firedOncePerSession(waDedupKey)) return
      handleAddToCart(subEvent, location, activeConfig, quantity, storageTime)
      trackEvent({
        action: EventAction.BrokerSignup,
        locationId: location.id,
        configurationId: activeConfig?.id,
        subType: subEvent
      })
      // `intentOnly: true` — a WA-pill click is upstream INTENT, already captured
      // above as the secondary `*_contact_*` event. It must NOT fire the primary
      // `*_lead` conversion endpoint; the true WhatsApp PRIMARY is the server-side
      // `qualified_whatsapp_lead` (LangGraph, on HubSpot deal creation).
      generateLead({
        method,
        locationType: activeConfig?.locationType ?? undefined,
        dealType: activeConfig?.dealType,
        monthlyPrice: activeConfig?.price ?? undefined,
        channel: 'whatsapp',
        intentOnly: true
      })
    })
    const msg = buildLeadFormWaMessage({
      variant: { meta: variant.meta, leadForm: variant.leadForm },
      location,
      configuration: activeConfig,
      values: {},
      locale: 'es',
      sessionToken: claimWaSessionToken(),
      clickIds: getClickIdsFromCookies()
    })
    const url = buildWhatsappUrl(msg)
    if (typeof window !== 'undefined') {
      window.open(url, '_blank')
    }
  }

  /**
   * LeadForm submission handler — replaces the legacy broker form. Same
   * mutation (`sendCustomerInfoEmail`), same cookie write, same follow-up
   * events. Adds the `channel` enrichment to `generateLead` so a single
   * Meta `Lead` event can be sliced by WA-vs-email funnel.
   */
  const handleLeadFormSubmit = useCallback(
    async (values: LeadFormValues, channel: LeadFormChannel) => {
      const trimmedName = values.name?.trim() ?? ''
      const trimmedEmail = values.email?.trim() ?? ''
      const trimmedPhone = values.phone?.trim() ?? ''
      const customerType = isFleetMode
        ? CustomerType.RentBroker
        : isTraditionalMode
          ? CustomerType.RentIndividual
          : CustomerType.RentBroker
      try {
        await sendCustomerInfoEmail({
          variables: {
            customerInfo: {
              name: trimmedName,
              lastname: '',
              email: trimmedEmail,
              phone: trimmedPhone,
              message: values.additionalInfo ?? '',
              interest: Interest.Rent,
              customerType,
              locationId: location.id,
              configId: activeConfig?.id,
              locationType: activeConfig?.locationType ?? undefined,
              leadSource: isFleetMode
                ? 'listing_fleet_modal'
                : isTraditionalMode
                  ? 'listing_traditional_modal'
                  : 'listing_broker_modal',
              storageTime:
                !isFleetMode && values.when
                  ? (values.when as StorageTime)
                  : undefined,
              quantity:
                isFleetMode && values.quantity && values.quantity > 0
                  ? values.quantity
                  : undefined,
              // Injected by LeadForm's BotID pre-flight — server keeps the
              // lead but skips the host notification when flagged.
              botIdFlagged: values.botIdFlagged
            }
          }
        })
      } catch (error) {
        logger.error('Error creating lead', { error })
        enqueueSnackbar(
          'Ha ocurrido un error al enviar la información, por favor intenta de nuevo',
          { variant: 'error' }
        )
        setCookie(null, 'lead', '', { maxAge: 0 })
        throw error
      }

      // Persist the lead cookie so returning visitors get pre-filled.
      setCookie(
        null,
        'lead',
        JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          phone: trimmedPhone,
          additionalInfo: values.additionalInfo ?? ''
        }),
        { maxAge: 30 * 24 * 60 * 60, path: '/' }
      )

      // Follow-up events. Method matches the legacy broker form branching.
      // `isTraditionalMode` (declared above) flows through the canonical
      // dealType resolver — no need to re-check the raw field here.
      const method = isFleetMode
        ? 'business_fleet'
        : isTraditionalMode
          ? 'traditional_contact'
          : 'broker'
      trackEvent({
        action: EventAction.BrokerSignup,
        locationId: location.id,
        configurationId: activeConfig?.id,
        subType: isTraditionalMode ? 'traditional_contact' : 'broker_contact'
      })
      generateLead({
        method,
        locationType: activeConfig?.locationType ?? undefined,
        dealType: activeConfig?.dealType,
        monthlyPrice: activeConfig?.price ?? undefined,
        channel
      })

      // For WhatsApp channel, redirect after submit (matches the legacy
      // `clicked === 'whatsapp'` branch).
      if (channel === 'whatsapp') {
        const msg = buildLeadFormWaMessage({
          variant: { meta: variant.meta, leadForm: variant.leadForm },
          location,
          configuration: activeConfig,
          values: {
            name: trimmedName,
            quantity: values.quantity,
            when: values.when,
            additionalInfo: values.additionalInfo
          },
          locale: 'es',
          sessionToken: claimWaSessionToken(),
          clickIds: getClickIdsFromCookies()
        })
        // v1: all WA leads route to the corporate number for chatbot/triage.
        // Specialist routing returns in a follow-up when sales-ops is
        // provisioned to absorb direct leads.
        const url = buildWhatsappUrl(msg)
        if (typeof window !== 'undefined') {
          window.open(url, '_blank')
        }
      }
    },
    [
      sendCustomerInfoEmail,
      trackEvent,
      EventAction,
      isFleetMode,
      isTraditionalMode,
      location,
      activeConfig,
      variant.meta,
      variant.leadForm,
      enqueueSnackbar,
      logger
    ]
  )

  /**
   * CTA dispatch — routes the primary CTA click based on the variant's
   * declared `primaryCta.action`. Replaces the inline branching that used
   * to live in each button's `onClick`.
   */
  const onPrimaryCtaClick = () => {
    switch (variant.priceCard.primaryCta.action) {
      case 'checkout':
        return handleOnCheckout()
      case 'open-lead-form':
        return handleOnBrokerClick()
      case 'open-whatsapp':
        return handleOnWhatsappCta()
      default:
        return undefined
    }
  }

  const handleOnCheckout = () => {
    if (loadingCurrentUser) return

    // Pre-auth /reservar funnel split — unauthed Monthly visitors get the
    // phone-OTP path instead of the email/password login modal. The
    // /reservar page fires its own `begin_checkout_preauth` event on
    // mount, so we skip the legacy `begin_checkout` fire here for the
    // unauthed cohort. Returning visitors with an email account stay on
    // the existing flow (this branch isn't reached when isAuthenticated).
    //
    // `isMonthlyVariant` is the canonical client-side dealType resolver
    // (src/utils/pricing.ts). It handles the null-as-Monthly fallback the
    // server's `resolveDealType()` applies, and also catches the
    // price>30k → Broker heuristic so a $35k listing with `dealType`
    // unset stays out of /reservar. Same gate runs server-side in the
    // /reservar page's getServerSideProps.
    if (!isAuthenticated && isMonthlyVariant(activeConfig)) {
      const urlRoute = location.urlRoute
      const qs = new URLSearchParams({
        q: String(quantity),
        c: configurationId ?? ''
      })
      if (storageTime) qs.set('st', storageTime)
      // urlRoute is virtually always populated for a verified listing, but
      // we don't strand the user on the rare case where it isn't — the
      // /reservar page resolves by configurationId server-side so a
      // configId-only path still finds the listing.
      const target = urlRoute
        ? `${urlRoute}/reservar?${qs.toString()}`
        : `/reservar?${qs.toString()}`
      Router.push(target)
      return
    }

    handleAddToCart(
      'begin_checkout',
      location,
      activeConfig,
      quantity,
      storageTime
    )
    if (!isAuthenticated) {
      toggleLoginModal(true, 'checkout')
      setPostLogin('checkout')
      return
    }

    setRedirecting(true)
    if (needsVerification) {
      const checkout = `${RedirectUrls.Checkout}/create/${id}?q=${quantity}&c=${configurationId}${storageTime ? `&st=${storageTime}` : ''}`
      const encodedRedirect = encodeURIComponent(checkout)
      Router.push(
        `${RedirectUrls.UserConfirmation}?redirectU=${encodedRedirect}&locationId=${id}&configurationId=${configurationId}&quantity=${quantity}${storageTime ? `&storageTime=${storageTime}` : ''}`
      )
    } else {
      createCheckout({
        variables: {
          locationId: id,
          configurationId,
          saveCheckout: {
            quantity,
            storageTime: storageTime || undefined
          }
        }
      })
      setRedirecting(true)
    }
  }

  const availabilityIsMultiple =
    availability !== undefined &&
    availability !== null &&
    availability > 1 &&
    !isBrokerMode

  const { showScarcity, scarcityCount } = getActiveScarcityCount(
    activeConfig,
    isBrokerMode,
    location.inventoryIsFresh
  )

  // Watching pill is rendered ONCE on mobile by Header (above the gallery)
  // and ONCE on desktop by Header — PriceCard intentionally does not
  // duplicate it on either breakpoint to keep the mobile sticky bar
  // compact and avoid colliding with the floating WhatsApp button.

  // Unit 12 — variant-specific scarcity chip. Branches on variantKey so each
  // t() call uses a literal locale key (the variant's `scarcityCopyKey` is
  // declarative-only; we don't pass it to t() directly because typed t()
  // rejects dynamic strings). Broker / Fleet were originally gated on a
  // server-driven `recentInquiriesCount`; that feature was never wired
  // through the listing query and the count always defaulted to 0, so it
  // was deleted. Broker/Fleet no longer render a scarcity chip here.
  const variantKey = variant.meta.variantKey
  // Traditional used to render a static «Anfitrión activo · responde <2h»
  // chip — a response-time promise nothing measures (and the Traditional
  // lead copy rule forbids). It now shows the same real, freshness-gated
  // scarcity as Monthly (`showScarcity` → isAvailabilityClaimable).
  const showVariantScarcityChip =
    variant.priceCard.showScarcity &&
    (variantKey === 'monthly' || variantKey === 'traditional') &&
    showScarcity &&
    !availabilityIsMultiple

  const [postLogin, setPostLogin] = useState<'checkout' | undefined>()
  useEffect(() => {
    if (!currentUser || postLogin !== 'checkout') return
    handleOnCheckout()
    setPostLogin(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, postLogin])

  const sqm = (size?.length ?? 1) * (size?.width ?? 1)
  const areaPrice = fprice / sqm

  const IVA_RATE = 0.16
  const basePrice = fprice * quantity
  const firstMonthPrice = basePrice * (1 - discountFraction)
  const discountSaving = basePrice - firstMonthPrice
  const displayPrice =
    hasDiscount && !isBrokerMode ? firstMonthPrice : basePrice
  const ivaAmount = displayPrice * IVA_RATE
  const totalWithIva = displayPrice + ivaAmount
  const regularTotalWithIva = basePrice + basePrice * IVA_RATE
  // The operator confirmed its own published price already INCLUDES IVA
  // (server: partnerPricing/ivaDisplay.ts). A Traditional price is the
  // operator's price, so adding 16% on top would overstate the renter's total
  // — LFPC 7 Bis wants the real total shown. SpotMe Now stays pre-IVA.
  const priceIncludesIva =
    isTraditionalMode && location.listPriceIncludesIva === true

  const broker = {
    value: areaPrice,
    unit: '/m²',
    iconBig: <CropFreeIcon className="mt-[-2px] mr-1 text-xl  text-black-500" />
  }
  const normal = {
    value: displayPrice,
    unit: `${currentCurrency}`,
    iconBig: null
  }
  const [big] = isBrokerMode ? [broker, normal] : [normal, broker]

  // Mobile sticky bar, line 2: the promo for the chip actually picked (the
  // SpotMe 50% or the operator's own offer), else the IVA total. With a
  // promo on line 2, line 1's unit carries «+ IVA» so the ex-IVA price is
  // never shown bare; the IVA-inclusive totals sit under the title.
  const mobilePromo = isBrokerMode
    ? null
    : promoBadgeContent(
        discount ?? null,
        operatorPromoPill(activeConfig?.operatorPromo)
      )
  const mobileUnit = isBrokerMode
    ? big.unit
    : hasDiscount
      ? priceIncludesIva
        ? '/1er mes'
        : t('PRICE_CARD.UNIT_FIRST_MONTH_PLUS_IVA')
      : mobilePromo && !priceIncludesIva
        ? t('PRICE_CARD.UNIT_MONTH_PLUS_IVA')
        : '/mes'

  const closeLeadForm = () => {
    setIsLeadFormOpen(false)
    setIsFleetMode(false)
    setIsLeadFormFromEmailCta(false)
  }

  /**
   * Fleet override for the LeadForm. The page's `useListingVariant()`
   * returns Monthly for SpotMe Now parking listings (Monthly's
   * `leadForm.enabled === false`), so a LeadForm rendered without an
   * override + isFleetMode=true reads Monthly from context, sees
   * `enabled: false`, and renders null → empty modal drawer. Passing
   * `baseFleetVariant.leadForm` directly flips the form into fleet mode
   * regardless of the underlying Monthly resolution.
   */
  const fleetVariantOverride = useMemo(
    () => ({
      meta: {
        // Route through the canonical resolver — fleet ALWAYS overlays
        // on a Monthly Parking config, but using `getEffectiveDealType`
        // keeps this aligned with every other dealType callsite in the
        // codebase. The Provider's resolver applies the same fallback.
        dealType: getEffectiveDealType(activeConfig),
        locationType: activeConfig?.locationType ?? LocationType.Parking
      },
      leadForm: baseFleetVariant.leadForm
    }),
    [activeConfig]
  )

  // Unavailable-state slim card. Replaces the full CTA tree (Reservar /
  // LeadForm / WA pill / fleet pill / inline trust signals) with a single
  // "Ver espacios similares" CTA + WA escape hatch. The page-level
  // <UnavailableBanner> already explains WHY (deleted / paused / sold_out)
  // so the slim card just acts as the in-rail action surface. Renders
  // BOTH desktop card + mobile sticky bar; both still honor the parent
  // `hideDesktopPriceCard` / `hideMobilePriceCard` flags so Details can
  // mount this component twice (once for each viewport) without
  // duplicating the bar. See root CLAUDE.md → "Unavailable listings".
  if (unavailableReason) {
    const reasonKey = unavailableReason === 'paused' ? 'PAUSED' : 'SOLD_OUT'
    const ctaCity = location.addressDetails?.city
    const waMessage = ctaCity
      ? t('UNAVAILABLE.WA_PREFILL_WITH_CITY', { city: ctaCity })
      : t('UNAVAILABLE.WA_PREFILL')
    const waHref = buildWhatsappUrl(waMessage)
    const handleSimilarClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!similarSectionId) return
      const el = document.getElementById(similarSectionId)
      if (!el) return
      e.preventDefault()
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    // Variant-aware CTA preference. Broker / Traditional listings are
    // specialist-mediated (the available-state PriceCard renders an inline
    // LeadForm in those variants, not a Reservar button) — so when the
    // listing is gone the next-best action is the WhatsApp escape hatch
    // (corporate WA → LangGraph triage → Fernando), not the SimilarLocations
    // rail. Monthly listings stay similars-first. Uses the canonical
    // pricing helper, not an inline dealType check. See spotme-client/CLAUDE.md
    // → "Unavailable listings — render in place, don't redirect".
    const dealType = getEffectiveDealType(activeConfig)
    const preferWhatsapp =
      dealType === DealType.Broker || dealType === DealType.Traditional

    const primaryClass =
      'inline-flex items-center justify-center rounded-full bg-blue-500 px-6 py-3 text-center text-[14px] font-semibold text-white-500 shadow-[0_20px_40px_-16px_rgba(0,164,255,0.55)] transition-all duration-200 hover:bg-blue-600 active:scale-[0.98] md:text-[15px]'
    const secondaryClass =
      'inline-flex items-center justify-center border border-ink-100/40 px-6 py-3 text-center text-[14px] font-medium text-ink-700 transition-colors hover:border-blue-500 hover:text-blue-500 md:text-[15px]'
    const similarLink = (cls: string) => (
      <a
        key="similar"
        href={similarSectionId ? `#${similarSectionId}` : '#'}
        onClick={handleSimilarClick}
        className={cls}
      >
        {t('UNAVAILABLE.SIMILAR_CTA')}
      </a>
    )
    const waLink = (cls: string) => (
      <a
        key="wa"
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        className={`${cls} gap-2`}
      >
        <i className="si si-chat text-base" />
        {t('UNAVAILABLE.WA_CTA')}
      </a>
    )

    return (
      <>
        {/* Desktop unavailable rail — same hairline-card frame as the
            normal PriceCard so the layout doesn't jump. */}
        <div
          className={`w-full ${hideDesktopPriceCard ? 'hidden' : 'hidden md:block'}`}
        >
          <div className="border border-ink-100/20 bg-white-500 px-7 py-6">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t(`UNAVAILABLE.${reasonKey}.EYEBROW`)}
            </p>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-display text-[28px] leading-none font-medium tracking-[-0.01em] text-ink-300 line-through md:text-[32px]">
                {`$${numberRounedUpWithoutDecimals(basePrice)}`}
              </span>
              <span className="font-display text-[12px] tracking-[0.14em] text-ink-200 uppercase">
                {t('UNAVAILABLE.PRICE_LABEL')}
              </span>
            </div>
            <p className="mt-3 max-w-xs text-[13px] leading-relaxed text-ink-200 md:text-[14px]">
              {t(`UNAVAILABLE.${reasonKey}.SUBTITLE`)}
            </p>
            <div className="mt-5 flex flex-col gap-2">
              {preferWhatsapp
                ? [waLink(primaryClass), similarLink(secondaryClass)]
                : [similarLink(primaryClass), waLink(secondaryClass)]}
            </div>
          </div>
        </div>

        {/* Mobile unavailable sticky bar — single line + compact CTA. */}
        <div
          className={`fixed inset-x-0 bottom-0 z-10 border-t border-ink-100/20 bg-white-500 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-12px_32px_-12px_rgba(18,38,63,0.18)] md:hidden ${hideMobilePriceCard ? 'hidden' : ''}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-[10px] tracking-[0.16em] text-blue-500 uppercase">
                {t(`UNAVAILABLE.${reasonKey}.EYEBROW`)}
              </p>
              <p className="mt-0.5 truncate text-[13px] font-medium text-ink-700">
                {t(`UNAVAILABLE.${reasonKey}.TITLE`)}
              </p>
            </div>
            {preferWhatsapp ? (
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-blue-500 px-4 py-2.5 text-center text-[13px] font-semibold text-white-500 shadow-[0_12px_24px_-10px_rgba(0,164,255,0.55)] active:scale-[0.98]"
              >
                <i className="si si-chat text-sm" />
                {t('UNAVAILABLE.WA_CTA')}
              </a>
            ) : (
              <a
                href={similarSectionId ? `#${similarSectionId}` : '#'}
                onClick={handleSimilarClick}
                className="inline-flex shrink-0 items-center justify-center rounded-full bg-blue-500 px-4 py-2.5 text-center text-[13px] font-semibold text-white-500 shadow-[0_12px_24px_-10px_rgba(0,164,255,0.55)] active:scale-[0.98]"
              >
                {t('UNAVAILABLE.SIMILAR_CTA')}
              </a>
            )}
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Lead-form sheet — modal drawer. Used by:
          - Mobile sticky-bar primary CTA on broker/traditional/fleet listings
            (the bar opens the drawer when the action falls through to
            `'open-lead-form'`, i.e. Fleet on Monthly+Parking).
          - Mobile sticky-bar BaseButton when the variant action is
            `'open-lead-form'` (legacy path).
          - Fleet pill (Monthly+Parking) — opens this drawer with the fleet
            variant override.
          - 2026-05-26: Desktop "Solicitar por correo" secondary button on
            broker/traditional listings (variant action === 'open-whatsapp').
            That path needs the drawer visible on desktop because the
            inline-on-card LeadForm is no longer rendered there.
          The wrapper is `md:hidden` EXCEPT for the WA-pill secondary path —
          on Fleet's Monthly desktop the form renders inline on the right
          rail (below), so showing the drawer too would overlay the inline
          form. Keep them mutually exclusive per surface. */}
      <div
        className={
          (isBrokerMode || isTraditionalMode) &&
          variant.priceCard.primaryCta.action === 'open-whatsapp'
            ? ''
            : 'md:hidden'
        }
      >
        <ModalDrawer
          isOpen={isLeadFormOpen}
          setIsOpen={(open) => {
            if (!open) closeLeadForm()
            else setIsLeadFormOpen(true)
          }}
        >
          <LeadForm
            container="mobile-sheet"
            leadSource={
              isFleetMode
                ? 'listing_fleet_modal'
                : isTraditionalMode
                  ? 'listing_traditional_modal'
                  : 'listing_broker_modal'
            }
            location={location}
            configuration={activeConfig}
            initialValues={leadFormPrefill}
            onSubmit={handleLeadFormSubmit}
            onClose={closeLeadForm}
            variantOverride={isFleetMode ? fleetVariantOverride : undefined}
            primaryChannelOverride={
              isLeadFormFromEmailCta ? 'email' : undefined
            }
          />
        </ModalDrawer>
      </div>

      {/* Desktop Price Rail — editorial hairline card on white. */}
      <div
        className={`w-full ${hideDesktopPriceCard ? 'hidden' : 'hidden md:block'}`}
      >
        <div className="border border-ink-100/20 bg-white-500 px-7 py-6">
          {/* Eyebrow + price block */}
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {hasDiscount && !isBrokerMode
              ? t('PRICE_CARD.RENT_FIRST_MONTH')
              : t('PRICE_CARD.RENT_PER_MONTH')}
          </p>

          {/* Price and promo badge share one wrapping row: the badge drops
              under the price when the rail is narrow instead of being pushed
              past its edge (clipped at 900–1023px, 2026-10-28 sweep). */}
          <div className="mt-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
              {hasDiscount && !isBrokerMode && (
                <span className="text-[14px] text-ink-200 line-through">
                  {`$${numberRounedUpWithoutDecimals(basePrice)}`}
                </span>
              )}
              <span
                className={`font-display text-[36px] leading-none font-medium tracking-[-0.01em] md:text-[44px] ${hasDiscount && !isBrokerMode ? 'text-promo-600' : 'text-ink-700'}`}
              >
                {big.iconBig}
                <span className="italic">
                  {`$${numberRounedUpWithoutDecimals(big.value)}`}
                </span>
                <span className="ml-1 font-display text-[14px] text-ink-200 not-italic md:text-[15px]">
                  {big.unit}
                </span>
              </span>
              {discount && (
                <PromoBadge
                  promo={{ kind: 'firstMonth', ...discount }}
                  placement="inline"
                />
              )}
            </div>
          </div>
          {discountNeedsLongerStay && listingDiscount && (
            <p className="mt-2 text-[12px] font-medium text-promo-700">
              {t('PRICE_CARD.DISCOUNT_NEEDS_LONGER_STAY', {
                percentage: listingDiscount.percentage,
                minMonths: listingDiscount.minMonths
              })}
            </p>
          )}
          <OperatorPromoNote
            promo={activeConfig?.operatorPromo}
            className="mt-3"
          />

          {/* No review score in this rail — and, since 2026-09-30, none in
              Header either: the listing score was admin-typed, not computed
              from stored reviews. Re-adding one here would also cost 32px of
              rail height on the tallest variant (Monthly + multi-unit +
              fleet), the budget that pushes the flotillas pill off a 13"
              viewport. See the rail-height note on the price-tail block
              below. */}

          {/* Watching pill — DESKTOP intentionally omitted. The Header
              already renders the watching pill on both breakpoints, and
              showing it here too would duplicate the indicator on desktop.
              The mobile sticky bar below DOES still render its own watching
              pill because the Header scrolls out of view as the user
              scrolls into the price decision. */}

          {/* Unit 12 — variant-specific scarcity chip. Monthly and
              Traditional show "Último disponible" for a single claimable
              unit (freshness-gated off-platform). Broker / Fleet have no
              scarcity chip (the recent-inquiry count that would have
              driven theirs was never wired in production — removed). */}
          {!availabilityIsMultiple && showVariantScarcityChip && (
            <p className="mt-3 ml-1 inline-flex items-center gap-1.5 font-display text-[11px] tracking-[0.16em] text-blue-500 uppercase md:text-xs">
              <span
                aria-hidden="true"
                className="inline-flex size-1 rounded-full bg-blue-500"
              />
              {t('SCARCITY.MONTHLY_LAST')}
            </p>
          )}

          {availabilityIsMultiple && (
            <div className="mt-4 flex items-center justify-between text-[12px] text-ink-200 md:text-[13px]">
              <span className="inline-flex items-center gap-1.5">
                <AddHomeWorkIcon
                  sx={{ fontSize: 14 }}
                  className="text-blue-500"
                />
                {`$${numberRounedUpWithoutDecimals(fprice)}/espacio`}
              </span>
              {showScarcity ? (
                <span className="inline-flex items-center gap-1.5 font-display text-[11px] tracking-[0.16em] text-blue-500 uppercase">
                  <span
                    aria-hidden="true"
                    className="inline-flex size-1 rounded-full bg-blue-500"
                  />
                  {scarcityCount === 1
                    ? t('SCARCITY.MONTHLY_LAST')
                    : t('SCARCITY.MONTHLY_REMAINING', {
                        count: scarcityCount
                      })}
                </span>
              ) : (
                // One template literal — `{availability}` adjacent to text
                // emitted a `<!-- -->` boundary comment at SSR that
                // comment-stripping intermediaries drop, firing #418
                // (Trigger H — hydration_diff 2026-07-15, deploy 46d17ab2).
                <span>
                  {t('SECTIONS.SPACES_AVAILABLE', { count: availability })}
                </span>
              )}
            </div>
          )}

          {/* Price tail. Two paths:
              - Monthly keeps the full expandable <dl> breakdown — it's
                the on-platform checkout actor and the renter expects to
                see Total + IVA + Ver desglose before committing $.
              - Broker / Traditional get a one-line "+ IVA = $X" subtext
                instead. Those flows route off-platform via the LeadForm
                that sits below; the breakdown's ~120px of card height
                pushed the right rail (PriceCard + Size Guide CTA) off
                the viewport on a 13" MacBook Pro. The renter still sees
                the total-with-IVA so we're not hiding anything material;
                the expandable detail (basePrice / discount / IVA split)
                lives in the LeadForm submission + the broker conversation
                that follows.
              Monthly keeps every price fact, but the tail is spacing-tight
              (mt-4/pt-4, IVA + 2°-mes on one line) because Monthly is the
              TALLEST variant — it's the only one that stacks the breakdown,
              the storage chips, the quantity row AND the fleet pill. */}
          {!isBrokerMode && !isTraditionalMode && (
            <dl className="mt-4 border-t border-ink-100/15 pt-4 text-[13px] md:text-sm">
              <div className="flex items-baseline justify-between">
                <dt className="font-display text-[12px] tracking-[0.16em] text-ink-700 uppercase md:text-[13px]">
                  {hasDiscount ? 'Total 1er mes' : 'Total'}
                </dt>
                <dd className="font-display text-[18px] font-medium text-ink-700 md:text-[20px]">
                  <span className="italic">
                    {`$${numberRounedUpWithoutDecimals(totalWithIva)}`}
                  </span>{' '}
                  <span className="text-[12px] text-ink-200">
                    {currentCurrency}
                  </span>
                </dd>
              </div>
              {/* IVA + the 2°-mes step-up share one line. Both facts are
                  still stated verbatim — this is a wrap, not a removal. */}
              <p className="mt-1 text-[12px] text-ink-200">
                {hasDiscount
                  ? `IVA incluido · A partir del 2° mes: $${numberRounedUpWithoutDecimals(regularTotalWithIva)} ${currentCurrency}/mes`
                  : 'IVA incluido'}
              </p>

              <button
                type="button"
                aria-expanded={breakdownOpen}
                onClick={() => setBreakdownOpen(!breakdownOpen)}
                className="mt-2 inline-flex items-center gap-1.5 font-display text-[10px] tracking-[0.16em] text-ink-200 uppercase transition-colors hover:text-blue-500 md:text-[11px]"
              >
                <span>
                  {breakdownOpen ? 'Ocultar desglose' : 'Ver desglose'}
                </span>
                <i
                  aria-hidden="true"
                  className={`si si-caret-down text-[10px] transition-transform ${breakdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {breakdownOpen && (
                <div className="mt-3 space-y-2 border-t border-ink-100/15 pt-3 text-ink-200">
                  <div className="flex items-center justify-between">
                    <dt>{t('PRICE_CARD.MONTHLY_RENT')}</dt>
                    <dd>
                      {`$${numberRounedUpWithoutDecimals(basePrice)} ${currentCurrency}`}
                    </dd>
                  </div>
                  {hasDiscount && (
                    <div className="flex items-center justify-between text-promo-700">
                      <dt>{`Descuento 1er mes (−${discount.percentage}%)`}</dt>
                      <dd>
                        {`−$${numberRounedUpWithoutDecimals(discountSaving)} ${currentCurrency}`}
                      </dd>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <dt>IVA (16%)</dt>
                    <dd>
                      {`$${numberRounedUpWithoutDecimals(ivaAmount)} ${currentCurrency}`}
                    </dd>
                  </div>
                </div>
              )}
            </dl>
          )}

          {(isBrokerMode || isTraditionalMode) && (
            <div className="mt-2 text-[12px] text-ink-200 md:text-[13px]">
              {priceIncludesIva ? (
                <p>IVA incluido</p>
              ) : (
                <p>
                  {`+ IVA = `}
                  <span className="font-medium text-ink-700">
                    {`$${numberRounedUpWithoutDecimals(totalWithIva)}`}
                  </span>
                  {isBrokerMode ? ' /mes (estimado)' : ' /mes'}
                </p>
              )}
              {hasDiscount && isTraditionalMode && (
                <p className="mt-0.5">
                  {`Después: $${numberRounedUpWithoutDecimals(priceIncludesIva ? basePrice : regularTotalWithIva)}/mes`}
                </p>
              )}
            </div>
          )}

          {/* Minimum months notice — Monthly only. Off-platform leads
              (Broker/Traditional) discuss term during the broker call,
              so a card-level pill is just extra height. */}
          {!isBrokerMode &&
            !isTraditionalMode &&
            activeConfig.minMonths &&
            activeConfig.minMonths > 1 && (
              <p className="mt-4 inline-flex items-center gap-1.5 font-display text-[11px] tracking-[0.16em] text-ink-200 uppercase md:text-xs">
                <i className="si si-hours text-[11px]" />
                {t('PRICE_CARD.MIN_MONTHS', { count: activeConfig.minMonths })}
              </p>
            )}

          {/* CTA region — Broker / Traditional, DESKTOP only (this whole
              card lives under `hidden md:block`). 2026-05-18: the form is
              now always-expanded inline; the legacy "Solicitar info" /
              "Rentar" CTA + replace-on-click toggle has been folded away
              to match Mexican marketplace conventions (Inmuebles24 /
              Pincali / propiedades.com). Mobile keeps its CTA → sheet
              flow via the sticky bottom bar (rendered separately below).

              Vertical stack: LeadForm on top (the primary surface), then
              the trust signals as a compact pill strip, then the optional
              insurance badge. Trust signals were a vertical divided list
              before — they're inline pills now so the rail stays under
              ~580px even on Broker (where Fernando also sits inside the
              form). Same content, denser rhythm. */}
          {!adminPageDisplay && (isBrokerMode || isTraditionalMode) && (
            <div className="mt-6">
              {/* Broker / Traditional desktop CTA. 2026-05-26 course-
                  correction (docs/plans/2026-05-26-001): the inline LeadForm
                  is replaced by a green WhatsApp pill that routes directly
                  to the SpotMe corporate WhatsApp via buildWhatsappUrl(msg)
                  — no recipientNumber arg. The WA conversation is triaged
                  by LangGraph (see "WhatsApp routing — trust face vs
                  destination number" in spotme-client/CLAUDE.md). Production
                  data over the previous 2 weeks showed the inline form
                  converted at 0.6-0.8% (engagement→submit) while the WA
                  pill converted at ~75% (click→qualified-lead). The handler
                  fires the full demand-side pixel fanout (add_to_cart +
                  broker_contact/traditional_contact + segmented variants +
                  internal ADD_TO_CART + generateLead with channel:whatsapp)
                  so PostHog + Google Ads + Meta CAPI all stay wired. */}
              {variant.priceCard.primaryCta.action === 'open-whatsapp' ? (
                <div>
                  <button
                    type="button"
                    onClick={handleOnWhatsappCta}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-green-500 px-7 py-3.5 text-center text-base font-semibold whitespace-nowrap text-white-500 transition-colors hover:bg-green-600 active:scale-[0.98]"
                  >
                    <Image
                      src={WhatsappIcon}
                      alt=""
                      width={18}
                      height={18}
                      aria-hidden="true"
                    />
                    {t(variant.priceCard.primaryCta.labelKey as never)}
                  </button>
                  {/* Outlined secondary CTA — opens the LeadForm drawer so
                      the user can submit by email (or WhatsApp from inside
                      the form — same channel split the form has always
                      surfaced). Reuses `handleOnBrokerClick` which sets
                      `isLeadFormOpen=true` and fires the engagement event
                      with `skipPixelFanout: true` (LeadForm Step 1 owns
                      the canonical addToCart fire). The drawer is mounted
                      at the top of the component and is made visible on
                      desktop specifically for this path — see the wrapper
                      gate below. */}
                  <button
                    type="button"
                    onClick={() => {
                      // User explicitly picked email — flip the override so
                      // the drawer's Step 2 opens with the blue email pill
                      // on top and the WhatsApp link as secondary. Reused
                      // engagement-event fire (skipPixelFanout) lives in
                      // `handleOnBrokerClick`; we wrap it to also set the
                      // email-channel flag before opening the drawer.
                      setIsLeadFormFromEmailCta(true)
                      handleOnBrokerClick()
                    }}
                    className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border border-ink-100/40 px-7 py-3 text-sm font-semibold text-ink-700 transition-colors hover:border-blue-500 hover:text-blue-500"
                  >
                    {t('CTA.EMAIL_SECONDARY')}
                  </button>
                  {/* Fleet pill on Traditional parking (2026-08-20 — first
                      Traditional parking operator, Entra). Same flotillas
                      lane as the Monthly+Parking pill: opens the LeadForm
                      drawer in fleet mode (B2B fields + quantity signal)
                      via `handleOnFleetClick`; the drawer wrapper is
                      already desktop-visible on this WA-pill path. Gate is
                      resolver-owned (`priceCard.showFleetCta`). Outlined
                      style (not a second green pill) so the primary WA CTA
                      keeps visual primacy. */}
                  {showFleetTrigger && (
                    <button
                      type="button"
                      onClick={handleOnFleetClick}
                      className="group mt-2 flex w-full items-center justify-center gap-2 rounded-full border border-ink-700 py-3 text-[12px] font-medium tracking-[0.14em] text-ink-700 uppercase transition-colors hover:border-blue-500 hover:text-blue-500 active:scale-[0.98]"
                    >
                      <i
                        aria-hidden="true"
                        className="si si-delivery-truck text-base"
                      />
                      <span>{t('FLEET_TRIGGER.CTA')}</span>
                    </button>
                  )}
                </div>
              ) : (
                <LeadForm
                  container="inline-on-card"
                  leadSource={
                    isFleetMode
                      ? 'listing_fleet_modal'
                      : isTraditionalMode
                        ? 'listing_traditional_modal'
                        : 'listing_broker_modal'
                  }
                  location={location}
                  configuration={activeConfig}
                  initialValues={leadFormPrefill}
                  onSubmit={handleLeadFormSubmit}
                  onClose={closeLeadForm}
                  variantOverride={
                    isFleetMode ? fleetVariantOverride : undefined
                  }
                />
              )}

              {/* Compact trust strip — inline pills under a hairline. Saves
                  ~80px versus the previous vertical divided list, which
                  matters once the WA pill + email-fallback + trust pills +
                  insurance badge are already stacked. Same per-signal
                  labels; same renderTrustSignal helper. */}
              <ul className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-ink-100/15 pt-4 md:gap-x-5">
                {variant.priceCard.trustSignals.map((signal) => {
                  const content = renderTrustSignal(signal, t)
                  if (!content) return null
                  return (
                    <li
                      key={signal}
                      className="inline-flex items-center gap-1.5 font-display text-[10px] tracking-[0.16em] text-ink-700 uppercase md:text-[11px]"
                    >
                      <i
                        className={`si ${content.icon} text-[13px]! text-blue-500`}
                      />
                      <span>{content.label}</span>
                    </li>
                  )
                })}
              </ul>

              {/* Insurance badge — variant gate. Today only the Broker
                  variant requests 'badge-only'. Monthly uses
                  'chubb-picker' which is mounted in the checkout flow
                  (not on the listing page) so there's nothing to render
                  here for that mode. */}
              {variant.sections.insurance.mode === 'badge-only' && (
                <p className="mt-3 inline-flex items-center gap-1.5 font-display text-[11px] tracking-[0.16em] text-blue-500 uppercase md:text-xs">
                  <i
                    aria-hidden="true"
                    className="si si-house-shield text-[13px]"
                  />
                  {t('INSURANCE_BADGE.AVAILABLE')}
                </p>
              )}
            </div>
          )}

          {!adminPageDisplay &&
            !isBrokerMode &&
            !isTraditionalMode &&
            isFleetMode &&
            isLeadFormOpen && (
              <div className="mt-6">
                <LeadForm
                  container="inline-on-card"
                  leadSource="listing_fleet_modal"
                  location={location}
                  configuration={activeConfig}
                  initialValues={leadFormPrefill}
                  onSubmit={handleLeadFormSubmit}
                  onClose={closeLeadForm}
                  variantOverride={fleetVariantOverride}
                />
              </div>
            )}

          {!adminPageDisplay &&
            !isBrokerMode &&
            !isTraditionalMode &&
            !(isFleetMode && isLeadFormOpen) && (
              <>
                {/* Unit 15 — storage time chips gated by variant flag.
                  Behavioral no-op today: monthly=true, others=false (which
                  matches the outer isBroker/isTraditional gate). */}
                {variant.priceCard.showStorageTimeChips && (
                  // Wraps instead of scrolling: on the 900–1279px rail the
                  // one-row scroller hid «1+ año» off the card edge (2026-10-28
                  // sweep) — the very chip that unlocks the 50%. Chips drop
                  // under the label only when the rail is too narrow, so the
                  // rail stays short where it fits (13" height budget).
                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    <span className="inline-flex shrink-0 items-center gap-1 font-display text-[10px] tracking-[0.14em] text-ink-200 uppercase md:text-[11px]">
                      <i
                        aria-hidden="true"
                        className="si si-hours text-[12px] text-blue-500"
                      />
                      {t('PRICE_CARD.STORAGE_TIME_LABEL')}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {MOBILE_STORAGE_CHIPS.map(({ label, value }) => (
                        <button
                          key={value}
                          type="button"
                          aria-pressed={storageTime === value}
                          onClick={() =>
                            setStorageTime(storageTime === value ? '' : value)
                          }
                          className={`shrink-0 border px-2.5 py-1 text-[11px] font-medium tracking-[0.04em] transition-colors ${
                            storageTime === value
                              ? 'border-blue-500 bg-blue-500 text-white-500'
                              : 'border-ink-100/30 bg-white-500 text-ink-700 hover:border-blue-500'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Unit 15 — quantity input gated by variant flag (Monthly +
                  multi-unit only). Resolver already computes this; we read
                  through the variant.
                  The "· N disponibles" count is SUPPRESSED when the
                  availability row above already rendered it (both fire on the
                  same multi-unit condition, so the card was stating the same
                  number twice ~120px apart). The stepper itself carries the
                  affordance; `maxValue` still clamps to availability. */}
                {variant.priceCard.showQuantityInput && (
                  <p className="mt-4 inline-flex items-center gap-1.5 font-display text-[11px] tracking-[0.16em] text-ink-200 uppercase">
                    Cantidad
                    {!availabilityIsMultiple && (
                      <>
                        <span aria-hidden="true" className="text-ink-100">
                          ·
                        </span>
                        <span className="text-ink-700">{availability}</span>
                        disponibles
                      </>
                    )}
                  </p>
                )}
                <div
                  className={`${variant.priceCard.showQuantityInput ? 'mt-2' : 'mt-4'} flex items-stretch gap-3`}
                >
                  {variant.priceCard.showQuantityInput && (
                    <div className="w-36 shrink-0">
                      <InputNumber
                        value={quantity}
                        setValue={setQuantity}
                        maxValue={availability ?? 0}
                        size="large"
                      />
                    </div>
                  )}
                  <BaseButton
                    size="large"
                    variant="contained"
                    rounded
                    loading={loading}
                    onClick={quantity > 0 ? handleOnCheckout : undefined}
                    disabled={notAvailable || userIsOwner}
                    fullWidth
                  >
                    {resumeMonthly ? t('CTA.RESUME_RESERVATION') : 'Reservar'}
                  </BaseButton>
                </div>
                {/* Unit 15 — Monthly trust grid also reads from
                  variant.priceCard.trustSignals so all variants share the
                  same rendering path. Kept as a divided vertical list (NOT
                  converted to Broker's inline pill strip) because the Monthly
                  labels are full sentences — "No se cobra hasta que
                  aprueben" — which wrap to one-per-row as pills anyway, so
                  the strip would save nothing and read worse. Density comes
                  from the row padding instead: py-1.5 + 12px text. */}
                <div className="mt-4 divide-y divide-ink-100/15 border-t border-ink-100/15">
                  {variant.priceCard.trustSignals.map((signal) => {
                    const content = renderTrustSignal(signal, t)
                    if (!content) return null
                    return (
                      <div
                        key={signal}
                        className="flex items-center gap-2.5 py-1.5"
                      >
                        <i
                          className={`si ${content.icon} text-sm text-blue-500`}
                        />
                        <span className="text-[12px] text-ink-700 md:text-[13px]">
                          {content.label}
                        </span>
                      </div>
                    )
                  })}
                </div>

                {/* Fleet trigger — parking only, multi-unit (> 5 available).
                  2026-05-26 (docs/plans/2026-05-26-001): visually
                  WhatsApp-branded (bg-green-500 + WhatsApp icon) so it
                  reads as the same family of CTAs as the primary WA pill
                  on Broker/Traditional listings. Functionally UNCHANGED
                  — still opens the unified LeadForm in fleet mode (with
                  `variantOverride={baseFleetVariant}`), which captures
                  the quantity-input signal (high-value B2B fleet sales
                  cue) before routing the submit to WhatsApp. The truck
                  icon + "PLAN FLOTILLA · 10+" copy stays so renters
                  understand this is the multi-unit lane, not a direct
                  WhatsApp drop. */}
                {showFleetTrigger && (
                  <button
                    type="button"
                    onClick={handleOnFleetClick}
                    className="group mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-green-500 py-3 text-[12px] font-medium tracking-[0.14em] text-white-500 uppercase transition-colors hover:bg-green-600 active:scale-[0.98]"
                  >
                    <i
                      aria-hidden="true"
                      className="si si-delivery-truck text-base"
                    />
                    <span>{t('FLEET_TRIGGER.CTA')}</span>
                    <Image
                      src={WhatsappIcon}
                      alt=""
                      width={14}
                      height={14}
                      aria-hidden="true"
                    />
                  </button>
                )}
              </>
            )}
        </div>
      </div>

      {/* Mobile sticky bottom bar — keeps a soft lift since it floats over
          content; otherwise editorial: hairline top, white bg, italic price.
          Hidden when the LeadForm sheet is open (sheet sits over the bar
          anyway, but hiding prevents stale storage chips re-mounting).
          Broker / Traditional get a slightly taller bar (pt-4 vs pt-3) so the
          lone CTA + price row doesn't feel under-weight next to Monthly's
          chip-rich variant. Traditional matches Broker since the secondary
          email pill was folded into the LeadForm drawer. */}
      <div
        className={`fixed inset-x-0 bottom-0 z-10 border-t border-ink-100/20 bg-white-500 px-4 ${isBrokerMode || isTraditionalMode ? 'pt-4' : 'pt-3'} pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-12px_32px_-12px_rgba(18,38,63,0.18)] md:hidden ${hideMobilePriceCard || isLeadFormOpen ? 'hidden' : ''}`}
      >
        {!adminPageDisplay && !isBrokerMode && !isTraditionalMode && (
          <div className="mb-3 flex items-center gap-1.5">
            <span className="mr-0.5 inline-flex shrink-0 items-center gap-1 font-display text-[10px] tracking-[0.14em] text-ink-200 uppercase">
              <i
                aria-hidden="true"
                className="si si-hours text-[11px] text-blue-500"
              />
              <span className="hidden min-[390px]:inline">
                {t('PRICE_CARD.STORAGE_TIME_LABEL')}
              </span>
            </span>
            <div className="-mr-4 flex min-w-0 flex-1 [scrollbar-width:none] gap-1.5 overflow-x-auto pr-4 [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {MOBILE_STORAGE_CHIPS.map(({ label, value }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={storageTime === value}
                  onClick={() =>
                    setStorageTime(storageTime === value ? '' : value)
                  }
                  className={`shrink-0 border px-2.5 py-1 text-[11px] font-medium tracking-[0.04em] transition-colors ${
                    storageTime === value
                      ? 'border-blue-500 bg-blue-500 text-white-500'
                      : 'border-ink-100/30 bg-white-500 text-ink-700 hover:border-blue-500'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        {discountNeedsLongerStay && listingDiscount && (
          <p className="mb-2 text-[11px] font-medium text-promo-700">
            {t('PRICE_CARD.DISCOUNT_NEEDS_LONGER_STAY', {
              percentage: listingDiscount.percentage,
              minMonths: listingDiscount.minMonths
            })}
          </p>
        )}
        {/* Fleet trigger lives in Header for mobile (inline editorial pill in
            the listing body, near the watching pill) — keeps the sticky bar
            tight on small screens. Desktop fleet trigger stays in this card
            below the trust signals. */}
        {/* Mobile sticky bar intentionally OMITS the watching pill — the
            Header already renders it ABOVE the gallery (visible on first
            paint), so duplicating it here made the sticky bar too tall and
            crowded the floating WhatsApp button. Keep the bar compact. */}
        <div className="flex items-center justify-between gap-3">
          {/* Price info — TWO lines, always (Daniel, 2026-10-27: «reducir
              #s y agregar promo en rojo»). Line 1 is the price; line 2 is
              the promo badge when there is one, else the IVA total. Every
              fact that used to stack here (1er-mes total with IVA, 2°-mes
              price, «Último disponible», «Mín. N meses», units, the
              operator's full promo) is stated under the title instead —
              `ListingPriceFacts`. Nothing was dropped from the page. */}
          <div className="flex min-w-0 flex-col items-start gap-1">
            <div className="flex items-baseline gap-1.5 whitespace-nowrap">
              {/* Below 360px the struck price would squeeze the CTA column;
                  the rosa price + the «50%» badge carry the promo there. */}
              {hasDiscount && !isBrokerMode && (
                <span className="hidden text-xs text-ink-200 line-through min-[360px]:inline">
                  {`$${numberRounedUpWithoutDecimals(basePrice)}`}
                </span>
              )}
              <span
                className={`font-display text-[20px] leading-none font-medium ${hasDiscount && !isBrokerMode ? 'text-promo-600' : 'text-ink-700'}`}
              >
                {big.iconBig}
                <span className="italic">
                  {`$${numberRounedUpWithoutDecimals(big.value)}`}
                </span>
                <span className="ml-0.5 font-display text-[11px] text-ink-200 not-italic">
                  {mobileUnit}
                </span>
              </span>
            </div>

            {isBrokerMode ? (
              <span className="text-[11px] whitespace-nowrap text-ink-200">
                <span className="font-medium text-ink-700">
                  {`$${numberRounedUpWithoutDecimals(totalWithIva)}`}
                </span>
                /mes (IVA incl.)
              </span>
            ) : mobilePromo ? (
              <PromoBadge promo={mobilePromo} placement="inline" />
            ) : priceIncludesIva ? (
              // Operator price already includes IVA — no 16% added on top.
              <span className="text-[11px] whitespace-nowrap text-ink-200">
                IVA incluido
              </span>
            ) : (
              <span className="text-[11px] whitespace-nowrap text-ink-200">
                + IVA = $
                <span className="font-medium text-ink-700">
                  {numberRounedUpWithoutDecimals(totalWithIva)}
                </span>
              </span>
            )}

            {/* Only after the renter picked more than one unit: the headline
                is then the total, so the per-unit price must be stated. */}
            {availabilityIsMultiple && quantity > 1 && (
              <span className="flex items-center gap-1 text-[11px] text-ink-200">
                <AddHomeWorkIcon
                  sx={{ fontSize: 12 }}
                  className="shrink-0 text-blue-500"
                />
                <span>
                  <span className="font-medium text-ink-700">
                    {`$${numberRounedUpWithoutDecimals(fprice)}`}
                  </span>
                  {`/esp · `}
                  <span className="font-medium text-ink-700">
                    {availability}
                  </span>
                  {` disp.`}
                </span>
              </span>
            )}
          </div>

          {/* CTA Button — single pill on Broker / Traditional. 2026-05-26:
              when the variant's `primaryCta.action` is 'open-whatsapp', the
              pill renders WhatsApp-green and routes directly to the SpotMe
              corporate WhatsApp (via handleOnWhatsappCta). Otherwise it
              opens the bottom-sheet LeadForm (legacy path, currently used
              by Fleet override via `variantOverride={baseFleetVariant}`).
              Mobile screens are tight — we keep the trust microcopy under
              the CTA in both branches so the sticky bar reads the same
              way structurally regardless of channel. */}
          {!adminPageDisplay && (isBrokerMode || isTraditionalMode) && (
            <div className="flex min-w-0 flex-1 flex-col items-end gap-1">
              {variant.priceCard.primaryCta.action === 'open-whatsapp' ? (
                <button
                  type="button"
                  onClick={onPrimaryCtaClick}
                  className="inline-flex w-full items-center justify-center gap-1 rounded-full bg-green-500 px-3 py-2.5 text-sm font-semibold whitespace-nowrap text-white-500 transition-colors hover:bg-green-600 active:scale-[0.98]"
                >
                  <Image
                    src={WhatsappIcon}
                    alt=""
                    width={14}
                    height={14}
                    aria-hidden="true"
                  />
                  {t(variant.priceCard.primaryCta.labelKey as never)}
                </button>
              ) : (
                <BaseButton
                  size="medium"
                  variant="contained"
                  rounded
                  fullWidth
                  onClick={onPrimaryCtaClick}
                >
                  {isTraditionalMode ? t('CTA.RESERVE') : t('CTA.REQUEST_INFO')}
                </BaseButton>
              )}
              {/* Trust microcopy under the CTA — mirrors the SpotMe-Now
                  pattern (NO_CHARGE · FREE_CANCEL). Rendered for BOTH
                  Broker and Traditional so the sticky bar reads the same
                  way structurally between off-platform variants. Each
                  variant uses its own truthful copy:
                    • Traditional (Anexo C): "Sin compromiso · Asesor gratis"
                    • Broker (Anexo B / SpotMe Pro): "Sin compromiso · Asesor especializado"
                  Both labels: engaging the specialist does not bind the
                  renter; off-platform terms negotiate later. Anexo-A-only
                  claims (Chubb, 24h refund) are intentionally omitted —
                  they don't apply off-platform. */}
              {(isTraditionalMode || isBrokerMode) && (
                <span className="mt-0.5 text-right font-display text-[10px] leading-tight tracking-[0.08em] whitespace-nowrap text-ink-200 uppercase min-[360px]:tracking-[0.12em]">
                  {isTraditionalMode
                    ? t('TRUST_MOBILE.NO_COMMITMENT')
                    : t('TRUST.BROKER.NO_COMMITMENT')}
                  <span
                    aria-hidden="true"
                    className="mx-1 hidden text-ink-100 sl:inline"
                  >
                    ·
                  </span>
                  <span className="hidden text-blue-500 sl:inline">
                    {isTraditionalMode
                      ? t('TRUST_MOBILE.FREE_ADVISOR')
                      : t('TRUST.BROKER.SPECIALIST')}
                  </span>
                </span>
              )}
            </div>
          )}

          {!adminPageDisplay && !isBrokerMode && !isTraditionalMode && (
            <div className="flex min-w-0 flex-1 flex-col items-end gap-1">
              {!settingQuantity ? (
                <>
                  <BaseButton
                    size="medium"
                    variant="contained"
                    fullWidth
                    rounded
                    loading={loading}
                    onClick={
                      // Two-tap quantity step is only worthwhile for authed
                      // users — they're heading to /checkout where the
                      // quantity threads directly into the booking. For
                      // unauthed users we route to /reservar (phone-OTP
                      // capture) and quantity gets picked at the post-auth
                      // checkout's Date step instead — see
                      // `src/components/Checkout/steps/DateStep.tsx`. Skipping
                      // the step removes a tap with no signal value for the
                      // unauth cohort (quantity defaults to 1 → carried via
                      // `?q=1` → DateStep input lets the renter adjust).
                      availabilityIsMultiple && isAuthenticated
                        ? () => setSettingQuantity(true)
                        : handleOnCheckout
                    }
                    disabled={notAvailable || userIsOwner}
                  >
                    {resumeMonthly ? t('CTA.RESUME_RESERVATION') : 'Reservar'}
                  </BaseButton>
                  {/* One line, never two: «Cancela gratis 24h» joins from
                      650px — at 428–430px it wrapped and orphaned «24H»
                      (Daniel's 2026-10-27 screenshot). */}
                  <span className="mt-0.5 text-right font-display text-[10px] leading-tight tracking-[0.08em] whitespace-nowrap text-ink-200 uppercase min-[360px]:tracking-[0.12em]">
                    {t('TRUST_MOBILE.NO_CHARGE')}
                    <span
                      aria-hidden="true"
                      className="mx-1 hidden text-ink-100 sl:inline"
                    >
                      ·
                    </span>
                    <span className="hidden text-blue-500 sl:inline">
                      {t('TRUST_MOBILE.FREE_CANCEL')}
                    </span>
                  </span>
                </>
              ) : (
                <>
                  <InputNumber
                    value={quantity}
                    setValue={setQuantity}
                    maxValue={availability ?? 0}
                  />
                  <NextButton
                    onClickBack={() => setSettingQuantity(false)}
                    onClickNext={quantity > 0 ? handleOnCheckout : undefined}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Admin Controls — collapsible floating FAB (matches the pre-MR
          pattern from HEAD). Default: a small Tune icon. Expanded: vertical
          pill of 3 actions: AdminEdit modal · Edit listing · Host preview.
          Self-gates on `variant.admin` (null for non-admins / preview-as-
          anon, so no markup leak). Mobile bottom mirrors the WA FAB's
          per-variant offset (see WrapperWA.tsx) so both flank the screen
          at the same height: Broker → bottom-24, Traditional → bottom-28
          (slightly taller sticky bar), Monthly → bottom-36. */}
      {!adminPageDisplay && variant.admin && (
        <div
          className={`fixed left-4 z-20 ${isBrokerMode ? 'bottom-24' : isTraditionalMode ? 'bottom-28' : 'bottom-36'} md:bottom-8 md:left-6`}
        >
          {adminExpanded ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl bg-white-500/95 p-2.5 shadow-xl ring-1 ring-black-500/10 backdrop-blur-sm">
              <AdminEdit location={location} />
              <a
                href={`${RedirectUrls.Listings}/${id}/edit`}
                target="_blank"
                className="flex size-12 items-center justify-center rounded-full bg-red-500 text-white-500 transition-colors hover:bg-red-600"
                rel="noreferrer"
                aria-label={tCommon('A11Y.EDIT_LISTING')}
              >
                <EditIcon sx={{ fontSize: 20 }} />
              </a>
              <a
                href={`${RedirectUrls.Listings}/${id}`}
                target="_blank"
                rel="noreferrer"
                title={t('PRICE_CARD.HOST_PREVIEW')}
                aria-label={t('PRICE_CARD.HOST_PREVIEW')}
                className="flex size-12 items-center justify-center rounded-full bg-grey-600 text-white-500 shadow-lg transition-all hover:bg-grey-700 active:scale-95"
              >
                <VisibilityIcon sx={{ fontSize: 20 }} />
              </a>
              <button
                type="button"
                onClick={() => setAdminExpanded(false)}
                aria-label={tCommon('A11Y.CLOSE_ADMIN_TOOLS')}
                className="flex size-7 items-center justify-center rounded-full text-grey-600 transition-colors hover:bg-grey-200"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAdminExpanded(true)}
              aria-label={tCommon('A11Y.OPEN_ADMIN_TOOLS')}
              className="flex size-10 items-center justify-center rounded-full bg-black-500/60 text-white-500 shadow-lg backdrop-blur-sm transition-all active:scale-95"
            >
              <TuneIcon sx={{ fontSize: 18 }} />
            </button>
          )}
        </div>
      )}
    </>
  )
}

export default React.memo(PriceCard)
